import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Menu, Phone, MapPin, Mail, ChevronRight, MessageCircle, Instagram, Facebook, Calendar, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import ChatWidget from '@/components/ChatWidget';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/cd5s20vv_isalon2022-01.png";

const PriceListPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [startBookingOnOpen, setStartBookingOnOpen] = useState(false);

  const openChatForBooking = () => {
    setStartBookingOnOpen(true);
    setChatOpen(true);
  };

  const handleChatOpenChange = (open) => {
    setChatOpen(open);
    if (!open) {
      setStartBookingOnOpen(false);
    }
  };

  const priceCategories = [
    {
      title: "Cut & Finish",
      services: [
        { name: "Ladies Cut & Blow Dry", price: "from £45" },
        { name: "Ladies Cut Only", price: "from £35" },
        { name: "Gents Cut", price: "from £18" },
        { name: "Children's Cut (under 12)", price: "from £15" },
        { name: "Fringe Trim", price: "from £8" },
        { name: "Restyle", price: "from £55" }
      ]
    },
    {
      title: "Blow Dries",
      services: [
        { name: "Blow Dry - Short Hair", price: "from £20" },
        { name: "Blow Dry - Medium Hair", price: "from £25" },
        { name: "Blow Dry - Long Hair", price: "from £30" },
        { name: "Curly Blow Dry", price: "from £35" },
        { name: "Special Occasion Blow Dry", price: "from £40" }
      ]
    },
    {
      title: "Colouring",
      services: [
        { name: "Full Head Colour", price: "from £60" },
        { name: "Root Colour", price: "from £45" },
        { name: "Full Head Highlights", price: "from £85" },
        { name: "Half Head Highlights", price: "from £65" },
        { name: "T-Section Highlights", price: "from £50" },
        { name: "Balayage", price: "from £95" },
        { name: "Ombre", price: "from £90" },
        { name: "Colour Correction", price: "POA" },
        { name: "Toner", price: "from £20" }
      ]
    },
    {
      title: "Treatments",
      services: [
        { name: "Olaplex Treatment", price: "from £25" },
        { name: "Deep Conditioning Treatment", price: "from £15" },
        { name: "Scalp Treatment", price: "from £20" },
        { name: "Hair Glossing", price: "from £30" }
      ]
    },
    {
      title: "Proms & Events",
      services: [
        { name: "Prom Hair - Up Style", price: "from £50" },
        { name: "Prom Hair - Down Style", price: "from £40" },
        { name: "Event Styling", price: "from £45" },
        { name: "Party Hair", price: "from £40" }
      ]
    },
    {
      title: "Wedding Hair",
      services: [
        { name: "Bridal Hair - Trial", price: "from £50" },
        { name: "Bridal Hair - On the Day", price: "from £80" },
        { name: "Bridesmaid Hair", price: "from £50" },
        { name: "Mother of Bride/Groom", price: "from £45" },
        { name: "Flower Girl", price: "from £25" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="pricelist-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Price List | iSalon UK | Hair Salon Prices Southport</title>
        <meta name="description" content="View iSalon UK's full price list for cuts, colours, highlights, treatments and styling services. Competitive prices at Southport's premier hair salon." />
        <meta name="keywords" content="hair salon prices southport, haircut prices, colour prices, highlights prices, hair treatment prices" />
        <link rel="canonical" href="https://www.isalon.uk/prices" />
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center" data-testid="logo">
              <img src={LOGO_URL} alt="iSalon UK" className="h-10 md:h-12 w-auto" />
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">
                Home
              </Link>
              <Link to="/about" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">
                About
              </Link>
              <span className="nav-link font-sans text-sm font-medium text-isalonuk-accent">
                Price List
              </span>
              <Link to="/opportunities" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">
                Join Us
              </Link>
              <Button onClick={openChatForBooking} className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full px-6">
                Book Now
              </Button>
            </div>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <button className="p-2 rounded-lg text-white">
                  <Menu size={40} strokeWidth={2.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm bg-black border-l border-white/10 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-white/10">
                    <img src={LOGO_URL} alt="iSalon UK" className="h-10 w-auto mb-4" />
                    <SheetClose asChild>
                      <Button onClick={openChatForBooking} className="w-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full py-5 text-lg">
                        <Calendar className="mr-2" size={20} />
                        Book Now
                      </Button>
                    </SheetClose>
                  </div>
                  <div className="flex-1 p-6 overflow-y-auto">
                    <nav className="flex flex-col gap-4">
                      <SheetClose asChild>
                        <Link to="/" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Home
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/about" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          About
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <div className="py-3 px-4 rounded-xl font-sans text-lg font-medium text-isalonuk-accent bg-isalonuk-accent/10">
                        Price List
                      </div>
                      <SheetClose asChild>
                        <Link to="/opportunities" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Join Us
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/#contact" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Contact Us
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                    </nav>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 px-6 md:px-12" data-testid="pricelist-hero">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-isalonuk-accent transition-colors mb-8">
            <ArrowLeft size={18} />
            <span className="font-sans text-sm">Back to Home</span>
          </Link>
          
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-isalonuk-accent font-sans text-sm md:text-base font-medium tracking-widest uppercase mb-4">
              Our Services
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              Price List
            </h1>
            <p className="text-white/70 font-sans text-base md:text-lg leading-relaxed">
              All prices are starting from and may vary depending on hair length and thickness. Please contact us for a consultation.
            </p>
          </div>
        </div>
      </section>

      {/* Price List */}
      <section className="py-12 md:py-16 px-6 md:px-12 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {priceCategories.map((category, index) => (
              <Card key={index} className="border border-white/10 bg-zinc-900 rounded-2xl overflow-hidden" data-testid={`price-category-${index}`}>
                <CardHeader className="bg-isalonuk-accent/10 border-b border-white/10 p-6">
                  <CardTitle className="font-heading text-2xl font-bold text-white">{category.title}</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {category.services.map((service, serviceIndex) => (
                      <div key={serviceIndex} className="flex items-center justify-between">
                        <span className="text-white/80 font-sans text-sm">{service.name}</span>
                        <span className="text-isalonuk-accent font-sans font-semibold text-sm">{service.price}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-white/60 font-sans text-sm mb-6">
              Prices may vary. Please contact us for a free consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={openChatForBooking} size="lg" className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-semibold rounded-full px-8">
                <Calendar className="mr-2" size={18} />
                Book Appointment
              </Button>
              <a href="tel:01704500872">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-8">
                  <Phone className="mr-2" size={18} />
                  Call Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="mb-4">
                <img src={LOGO_URL} alt="iSalon UK" className="h-10 w-auto" />
              </div>
              <p className="text-white/60 font-sans text-sm leading-relaxed mb-1">
                Established in 2012
              </p>
              <p className="text-white/60 font-sans text-sm leading-relaxed mb-4">
                Experience · Knowledge · Skill · Quality
              </p>
              <div className="flex gap-3">
                <a href="https://www.facebook.com/226899317381783" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-isalonuk-accent transition-colors">
                  <Facebook size={18} className="text-white" />
                </a>
                <a href="https://www.instagram.com/isalonuk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-isalonuk-accent transition-colors">
                  <Instagram size={18} className="text-white" />
                </a>
                <a href="https://twitter.com/isalonuk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-isalonuk-accent transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-sans font-semibold text-white mb-4">Quick Links</h4>
              <nav className="flex flex-col gap-2">
                <Link to="/about" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">About Us</Link>
                <Link to="/prices" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">Price List</Link>
                <Link to="/opportunities" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">Join Us</Link>
                <Link to="/#contact" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">Contact</Link>
              </nav>
            </div>

            <div>
              <h4 className="font-sans font-semibold text-white mb-4">Contact</h4>
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-2">
                  <MapPin size={16} className="text-isalonuk-accent mt-1 flex-shrink-0" />
                  <span className="text-white/60 font-sans text-sm whitespace-nowrap">89B London St, Southport, PR9 0TX</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <a href="tel:01704500872" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">01704 500872</a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <a href="mailto:bookings@isalon.uk" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">bookings@isalon.uk</a>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <button onClick={openChatForBooking} className="text-white/60 font-sans text-sm hover:text-isalonuk-accent text-left">Request Appointment</button>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="text-white/40 font-sans text-sm">© {new Date().getFullYear()} iSalon UK. All rights reserved.</p>
              <p className="text-white/40 font-sans text-xs leading-relaxed max-w-lg">
                iSalon UK is a trading name of Scissors Limited, a company registered in England and Wales with company registration number 11875206.
              </p>
              <div className="flex flex-col items-center gap-1">
                <p className="text-isalonuk-accent font-sans text-sm font-medium">
                  Website designed and built by <a href="https://www.barton.uk" target="_blank" rel="noopener noreferrer" className="hover:underline">www.barton.uk</a>
                </p>
                <Link to="/privacy-policy" className="text-white/40 font-sans text-xs hover:text-isalonuk-accent transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Chat Widget */}
      <ChatWidget 
        isOpen={chatOpen} 
        onOpenChange={handleChatOpenChange} 
        startBookingOnOpen={startBookingOnOpen}
      />
    </div>
  );
};

export default PriceListPage;
